/**
 * Level 2 records-by-band — three tiles, each individually clickable to open its
 * records for the scoped selection. High carries a berry accent.
 * The list is an access point into the records behind each band, not a chart.
 */

import { useState } from 'react';

import { ConductScoreCircle } from '../../../../ui/conduct-score-circle';
import { BAND_COLORS, BAND_LABELS, BAND_RANGE } from '../constants';
import type { Band, BandRecords, Bands, HighBandRecord } from '../types';

function openRecord(datasetId: string, recordId: string) {
  window.open(`/v4/records/${datasetId}/${recordId}`, '_blank', 'noopener,noreferrer');
}

const BAND_ORDER: Band[] = ['low', 'mod', 'high'];

export function BandTiles({
  bands,
  bandRecords,
  datasetId,
}: {
  bands: Bands;
  bandRecords: BandRecords;
  datasetId: string;
}) {
  const [openBand, setOpenBand] = useState<Band | null>(null);
  const records: HighBandRecord[] =
    openBand && bandRecords[openBand].length ? bandRecords[openBand] : [];

  return (
    <div>
      <div className="mt-0 flex items-center justify-between border-b border-[var(--soft,#ECECEE)] pb-2 text-[9px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground">
        <span>Records by band</span>
        <span>Records</span>
      </div>

      <div className="mt-3.5 grid grid-cols-1 gap-3 md:grid-cols-3">
        {BAND_ORDER.map((k) => {
          const isHigh = k === 'high';
          const count = Math.round(bands[k]);
          const canOpen = count > 0 && bandRecords[k].length > 0;
          const isOpen = openBand === k && canOpen;
          const cls = `w-full rounded-[var(--radius-sm)] border p-3.5 text-left transition-all ${
            canOpen ? 'cursor-pointer hover:shadow-[var(--shadow-card)]' : ''
          }`;
          const style = {
            borderColor: isHigh ? 'rgba(157,11,77,.35)' : 'hsl(var(--border))',
            background: isHigh ? 'rgba(157,11,77,.03)' : undefined,
            ...(isOpen ? { outline: `1.5px solid ${BAND_COLORS[k]}`, outlineOffset: '-1px' } : {}),
          };
          const inner = (
            <>
              <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-muted-foreground">
                <i className="h-2 w-2 rounded-full" style={{ background: BAND_COLORS[k] }} />
                {BAND_LABELS[k]}
              </div>
              <div className="my-1 text-2xl font-extrabold tracking-[-0.03em]">
                {count.toLocaleString('en-GB')}
              </div>
              <div className="text-[10px] font-semibold text-muted-foreground">{BAND_RANGE[k]}</div>
              <div className="mt-1 text-[10px] font-extrabold" style={{ color: canOpen ? BAND_COLORS[k] : undefined }}>
                {count === 0 ? (
                  <span className="text-muted-foreground">None in this period</span>
                ) : canOpen ? (
                  isOpen ? 'Hide records ×' : 'View records →'
                ) : (
                  <span className="text-muted-foreground">No sample records</span>
                )}
              </div>
            </>
          );
          return canOpen ? (
            <button
              key={k}
              type="button"
              onClick={() => setOpenBand(isOpen ? null : k)}
              className={cls}
              style={style}
            >
              {inner}
            </button>
          ) : (
            <div key={k} className={cls} style={style}>
              {inner}
            </div>
          );
        })}
      </div>

      {records.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-[var(--soft,#ECECEE)] pt-3">
          {openBand && Math.round(bands[openBand]) > records.length && (
            <div className="pb-1 text-[10px] font-semibold text-muted-foreground">
              Showing top {records.length} of {Math.round(bands[openBand]).toLocaleString('en-GB')}
            </div>
          )}
          {records.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => openRecord(datasetId, r.id)}
              className="flex w-full items-center gap-3 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-muted"
            >
              <ConductScoreCircle score={r.score} size="sm" showDecimal />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{r.headline}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="truncate">
                    {[r.channel, r.occurredAt].filter(Boolean).join(' · ')}
                  </span>
                  <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">
                    {r.outcomeName}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
